import type { ProductColor, ProductMockupTemplate } from '@/api/products';
import { proxyStorageUrl } from '@/lib/product-transformer';
import { HEATHER_PATTERN_FALLBACK } from '@/utils/mockupLayers';
import type { EditorOverlay } from './PrintAreaEditor';

/** Which optional overlays the admin preview shows. */
export interface EditorLayerVisibility {
  heathered: boolean;
  texture: boolean;
  shadow: boolean;
  overlay: boolean;
}

export const ALL_EDITOR_LAYERS_VISIBLE: EditorLayerVisibility = {
  heathered: true,
  texture: true,
  shadow: true,
  overlay: true,
};

/**
 * The overlay layers of a mockup template as the admin print-area editor
 * shows them, with the creator's defaults: the heathered layer appears only
 * for a heathered colour (falling back to the default heather pattern when
 * the template has none), texture and shadow multiply, the top overlay sits
 * on top unblended.
 */
export function buildEditorOverlays(
  template: ProductMockupTemplate | undefined,
  color: ProductColor | undefined,
  visibility: EditorLayerVisibility = ALL_EDITOR_LAYERS_VISIBLE
): EditorOverlay[] {
  if (!template) return [];
  const result: EditorOverlay[] = [];
  const heatheredUrl =
    proxyStorageUrl(template.heatheredOverlayUrl) ??
    (color?.isHeathered ? HEATHER_PATTERN_FALLBACK : null);
  if (heatheredUrl) {
    result.push({
      id: 'heathered',
      url: heatheredUrl,
      blendMode: template.heatheredBlendMode ?? 'OVERLAY',
      opacity: template.heatheredOpacity ?? 0.5,
      visible: visibility.heathered,
    });
  }
  const textureUrl = proxyStorageUrl(template.textureOverlayUrl);
  if (textureUrl) {
    result.push({
      id: 'texture',
      url: textureUrl,
      blendMode: template.textureBlendMode ?? 'MULTIPLY',
      opacity: template.textureOpacity ?? 0.4,
      visible: visibility.texture,
    });
  }
  const shadowUrl = proxyStorageUrl(template.shadowOverlayUrl);
  if (shadowUrl) {
    result.push({
      id: 'shadow',
      url: shadowUrl,
      blendMode: template.shadowBlendMode ?? 'MULTIPLY',
      opacity: template.shadowOpacity ?? 0.3,
      visible: visibility.shadow,
    });
  }
  const topOverlayUrl = proxyStorageUrl(template.topOverlayUrl);
  if (topOverlayUrl) {
    result.push({
      id: 'overlay',
      url: topOverlayUrl,
      blendMode: 'NORMAL',
      opacity: 1,
      visible: visibility.overlay,
    });
  }
  return result;
}

/** The cutout mask matching the large preview image (rendering asset first). */
export function editorCutoutMaskUrl(
  template: ProductMockupTemplate | undefined
): string | null {
  return (
    proxyStorageUrl(
      template?.renderingCutoutMaskUrl ?? template?.cutoutMaskUrl
    ) ?? null
  );
}

/**
 * The colour layer under the shading map, as the creator paints it: only
 * when the template has a cutout mask to clip it to the garment. A template
 * without a mask carries its colour in the base image; a colour layer would
 * fill the whole mockup rectangle.
 */
export function editorColorLayer(
  template: ProductMockupTemplate | undefined,
  color: ProductColor | undefined
): string | null {
  if (!color || !editorCutoutMaskUrl(template)) return null;
  return color.hexCode;
}
